import type {
  DictationEngine,
  DictationError,
  DictationHandlers,
  DictationSession,
} from './dictation-engine';

// The browser SpeechRecognition API (Chrome, Edge, Safari; not Firefox).
// Chrome and Edge send the audio to their vendor's speech service (Google,
// Microsoft) to transcribe it; nothing is recorded or stored by the app. A
// server engine can replace this one through DICTATION_ENGINE.

interface RecognitionResult {
  readonly isFinal: boolean;
  readonly 0: { readonly transcript: string };
}

interface RecognitionEvent {
  readonly results: ArrayLike<RecognitionResult>;
}

export interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

type RecognitionConstructor = new () => SpeechRecognitionLike;

function recognitionConstructor(scope: unknown): RecognitionConstructor | null {
  const w = scope as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  } | null;
  return w?.SpeechRecognition ?? w?.webkitSpeechRecognition ?? null;
}

export function dictationErrorFor(error: string): DictationError {
  switch (error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'denied';
    case 'audio-capture':
      return 'no-microphone';
    case 'no-speech':
      return 'no-speech';
    default:
      return 'failed';
  }
}

export function transcriptOf(results: ArrayLike<RecognitionResult>): string {
  return Array.from(results, (r) => r[0].transcript.trim())
    .filter(Boolean)
    .join(' ');
}

export class WebSpeechDictationEngine implements DictationEngine {
  private readonly create: RecognitionConstructor | null;

  constructor(scope: unknown = typeof window === 'undefined' ? null : window) {
    this.create = recognitionConstructor(scope);
  }

  get supported(): boolean {
    return !!this.create;
  }

  start(handlers: DictationHandlers): DictationSession {
    if (!this.create) {
      handlers.error('failed');
      handlers.ended();
      return { stop: () => undefined };
    }
    const recognition = new this.create();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) =>
      handlers.text(transcriptOf(event.results));
    recognition.onerror = (event) =>
      handlers.error(dictationErrorFor(event.error));
    recognition.onend = () => handlers.ended();
    try {
      recognition.start();
    } catch {
      handlers.error('failed');
      handlers.ended();
    }
    return { stop: () => recognition.stop() };
  }
}
